import { OnApproveData } from '@paypal/paypal-js';

export interface IVervAPIServices {
	location?: string;
	ping: () => Promise<Status>;
	getPaypalConfig: () => Promise<PaypalConfigData>;
	getStripeConfig: () => Promise<StripeConfigData>;
	registerPaypalSubscription: (
		dataToSend: PayPalRegisterSubscriptionRequest,
	) => Promise<PayPalRegisterSubscriptionResponse>;
	createStripeSubscription: (
		dataToSend: StripeCreateSubscriptionRequest,
	) => Promise<StripeCreateSubscriptionResponse>;
	intentStripePayment: (paymentIntentId: string) => Promise<StripeIntentPaymentResponse>;
	getWebCatalogProduct: (getWebCatalogProduct: string) => Promise<WebCatalogProductResponse>;
	sendBiData: (BiDataRequestBody: BiDataRequest) => Promise<WebExtraDataResponse>;
	sendUserData: (userData: IUserData) => Promise<WebExtraDataResponse>;
	changeStripeSubscription: (
		data: StripeChangeSubscriptionRequest,
	) => Promise<StripeChangeSubscriptionResponse>;
	purchaseProduct: (
		dataToSend: StripePurchaseProductRequest,
	) => Promise<StripePurchaseProductResponse>;
	createPaypalOrder: (
		dataToSend: PayPalPurchaseProductRequest,
	) => Promise<PayPalCreateOrderResponse>;
	registerPaypalOrder: (order: OnApproveData) => Promise<PayPalCaptureOrderResponse>;
}

export class APIError extends Error {
	readonly type?: string;

	constructor(message: string, type?: string) {
		super(message);
		this.type = type;
		this.name = 'API Error';

		Object.setPrototypeOf(this, APIError.prototype);
	}
}

export enum Status {
	success = 'OK',
	failure = 'FAIL',
}

export interface Success<T> {
	status: Status;
	data: T;
}

export interface Failure<E> {
	status: Status;
	error: E;
}

// parameters we want to be added into resulting onelink URL as query parameters
export interface OneLinkParameters {
	pid?: string;
	c?: string;
	af_prt?: string;
	af_adset?: string;
	af_ad?: string;
	utm_id?: any;
	deep_link_value: any;
}

export interface ExtraData {
	fbp?: string;
	gender?: string;
	language: string;
	source_url: string;
	source_key: string;
	pid: any;
	campaign: any;
}

export enum SUBSCRIPTION_SOURCE {
	ACADEMY = 'weight_loss_academy',
}

export interface IUserData {
	bundle_id: string;
	promocode?: string;
	marketing_consent: boolean;
	email?: string;
	source?: SUBSCRIPTION_SOURCE;
	extra_data: {
		source_url?: string;
		user_data?: IUserDataAnswers;
	};
}

export type IUserDataAnswers = Record<string, any>;

// register PayPal subscription
// according to https://verv.atlassian.net/wiki/spaces/~28650514/pages/4038721537/MFU+Web+payment+incidents
export interface PayPalRegisterSubscriptionRequest {
	bundle_id: string;
	email: string;
	fbclickid?: string;
	product: any;
	subscription_id: string;
	payment_link: string;
	onelink_query_params?: OneLinkParameters;
	tmp_web_extra_data?: string;
	user_agent?: string;
	ip_address?: string;
	template_id?: string;
	paypal_extra_info?: any;
	extra_data?: ExtraData;
}

export interface RegisterSubscriptionData {
	code: string;
	onelink: string;
	promocode: string;
}

export type PayPalRegisterSubscriptionResponse =
	| Success<RegisterSubscriptionData>
	| Failure<APIError>;

// Create Stripe subscription
export interface StripeCreateSubscriptionRequest {
	payment_method_id?: string;
	customer_email?: string;
	payment_link: string;
	fbclickid?: string;
	product: string;
	onelink_query_params: any;
	tmp_web_extra_data?: string;
	extra_data: ExtraData;
}

export interface StripeChangeSubscriptionRequest {
	email: string;
	product: string;
	extra_data?: ExtraData;
	use_existing_web_extra_data?: boolean;
	upgrade_to_lifetime_price?: string;
}

export interface StripeChangeSubscriptionData {
	client_secret: string;
	onelink: string;
	code: number;
}

export type StripeChangeSubscriptionResponse =
	| Success<StripeChangeSubscriptionData>
	| Failure<APIError>;

export interface StripeCreateSubscriptionData {
	code?: string | null;
	onelink?: string | null;
	requires_action: string;
}

export type StripeCreateSubscriptionResponse =
	| Success<StripeCreateSubscriptionData>
	| Failure<APIError>;

// Stripe Purchase Product
export interface StripePurchaseProductRequest {
	payment_method_id?: string;
	email: string;
	payment_link: string;
	fbclickid?: string;
	product: string;
	onelink_query_params: any;
	tmp_web_extra_data?: string;
	use_existing_payment_method: boolean;
}

export type StripePurchaseProductResponse = Success<StripePurchaseProductData> | Failure<APIError>;

export interface StripePurchaseProductData {
	clientSecret: string;
}

// PayPal Purchase Product
export interface PayPalPurchaseProductRequest {
	bundle_id: string;
	email: string;
	fbclickid?: string;
	product: any;
	payment_link: string;
	onelink_query_params?: OneLinkParameters;
	tmp_web_extra_data?: string;
	user_agent?: string;
	ip_address?: string;
	template_id?: string;
	paypal_extra_info?: any;
	extra_data?: ExtraData;
}

export type PayPalCreateOrderResponse = Success<PayPalPurchaseProductData> | Failure<APIError>;

export interface PayPalPurchaseProductData {
	order_id: string;
}

// PayPal register order
export type PayPalCaptureOrderResponse = Success<PayPalCaptureOrderData> | Failure<APIError>;

export interface PayPalCaptureOrderData {
	data: any;
}

export interface StripeConfigData {
	status: string;
	data: {
		publishableKey: string;
	};
}

export interface PaypalConfigData {
	status: string;
	data: {
		client_id: string;
	};
}

export type StripeIntentPaymentResponse = Success<RegisterSubscriptionData> | Failure<APIError>;

export interface ProductDuration {
	unit: string;
	value?: number | null;
}
export interface WebCatalogProduct {
	amount: number;
	total: number | null;
	total_excluding_tax: number | null;
	tax: number | null;
	tax_rate: number | null;
	currency?: string;
	intro_amount?: number | null;
	intro_total?: number | null;
	intro_total_excluding_tax?: number | null;
	intro_tax?: number | null;
	intro_tax_rate?: number | null;
	duration: ProductDuration;
	paypal_plan_id: string;
	trial_period_days?: number | null;
	country?: string;
}

export interface WebCatalogProductResponse {
	status: string;
	data: WebCatalogProduct;
}

export interface BiDataRequest {
	bundle_id: string;
	promocode?: string;
	marketing_consent?: boolean;
	email?: string;
	extra_data: {
		source_url: string;
		user_data?: any;
		bi_data?: {
			[x: string]: boolean;
		};
	};
}

export interface UserPromocode {
	promocode: string;
	onelink?: string;
}

export type WebExtraDataResponse = Success<UserPromocode> | Failure<APIError>;

export enum API_ERROR_TYPE {
	ACTIVE_SUBS_CONFLICT = 'ACTIVE_SUBS_CONFLICT',
	UNVERIFIED_CARD = 'do_not_honor',
	EXPIRED_CARD = 'expired_card',
	DECLINED_CARD = 'generic_decline',
	INCORRECT_CVC = 'incorrect_cvc',
	INCORRECT_CARD_NUMBER = 'incorrect_number',
	INSUFFICIENT_FUNDS = 'insufficient_funds',
	INVALID_ACCOUNT = 'invalid_account',
	INVALID_AMOUNT = 'invalid_amount',
	INVALID_CVC = 'invalid_cvc',
	LOST_CARD = 'lost_card',
	PROCESSING_ERROR = 'processing_error',
	STRIPE_ERROR = 'STRIPE_ERR',
	TRANSACTION_NOT_ALLOWED = 'transaction_not_allowed',
	DECLINED_CARD_TRY_AGAIN_LATER = 'try_again_later',
}
